"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import { ReactTable } from "@/app/(components)/Basic/ReactTable";
import { DropdownInput } from "@/app/(components)/Basic/DropdownInput";
import SortHamburgerIcon from "@/assets/icons/sort-hamburger.svg";
import {
  ActivityFilterOptionValue,
  ACTIVITY_FILTER_OPTIONS,
  ActivityFilterOption,
} from "@/app/(components)/Application/Activities/types";
import { useActivities } from "../hooks/useActivities";
import { NftActivityDescription } from "../../Nft/NftActivityDescription";
import { Date as DateComponent } from "@/app/(components)/Basic/Date";
import BasicUserProfile from "@/assets/graphics/default-profile.svg?url";
import { NoItems } from "@/app/(components)/Basic/NoItems";
import { ImageTypeHandler } from "@/app/(components)/Basic/ImageTypeHandler";
import { activityConfig } from "@/app/(components)/Application/Activities/types";
import { Activity } from "@/api/activities/types";

const columns = [
  {
    Header: "",
    accessor: "nft",
    Cell: ({ row }: { row: { original: Activity } }) => {
      const link = useMemo(
        () =>
          row.original.nft
            ? `/nft/${row.original.nftId}`
            : `/collection/${row.original.collection.slug}`,
        [row.original],
      );

      return (
        <div className="flex items-center gap-x-4 lg:gap-x-10 w-full">
          <Link href={link} className="relative box-content w-20 h-20 shrink-0">
            <ImageTypeHandler
              alt=""
              src={
                row.original.nft?.thumbnail ||
                row.original.nft?.image ||
                row.original.collection?.imageUrl ||
                BasicUserProfile.src
              }
              placeholder={row.original.nft?.placeholder}
              type={row.original.nft?.mediaType}
              fill
              className="object-cover"
            />
          </Link>
          <div className="flex flex-col">
            <Link href={link} className="text-lg lg:text-3xl font-light">
              {row.original.nft?.name || row.original.collection?.name}
            </Link>
            <span className="text-secondary">
              <NftActivityDescription
                activity={row.original}
                activityConfig={activityConfig}
              />
            </span>
          </div>
        </div>
      );
    },
  },
  {
    Header: "",
    accessor: "createdAt",
    Cell: ({ row }: { row: { original: Activity } }) => (
      <div className="w-full flex justify-end lg:justify-center items-center">
        <DateComponent value={row.original.createdAt} displayTime={true} />
      </div>
    ),
  },
];

interface Props {
  queryKey: string;
  initiatorIds: string[];
  collectionAddress?: string | null;
  activityFilterOptions?: ActivityFilterOption[];
}

const ActivitiesTable = ({
  queryKey,
  initiatorIds,
  collectionAddress,
  activityFilterOptions = ACTIVITY_FILTER_OPTIONS,
}: Props) => {
  const [filter, setFilter] = useState<ActivityFilterOptionValue>(
    ActivityFilterOptionValue.All,
  );
  const nftActivityTypes =
    activityFilterOptions.find((option) => option.value === filter) ||
    activityFilterOptions[0];

  const { activities, isLoading, hasNextPage, fetchNextPage } = useActivities(
    queryKey,
    initiatorIds,
    nftActivityTypes.types,
    collectionAddress,
  );

  return (
    <div className="flex flex-col items-center lg:items-end p-6 lg:p-0">
      <div className="w-80 mb-9">
        <DropdownInput
          text={"Show:"}
          selectedOption={filter}
          setSelectedOption={(filter: ActivityFilterOptionValue) =>
            setFilter(filter)
          }
          options={activityFilterOptions}
          startIcon={<SortHamburgerIcon />}
          className="w-full h-10 px-5 py-2"
        />
      </div>
      {isLoading && <NoItems message="Items loading" />}
      <ReactTable
        columns={columns}
        data={activities}
        hideColumnHeaderOnMobile
        showMoreButton={hasNextPage}
        showMoreHandler={fetchNextPage}
        isLoading={isLoading}
        rowClassName="flex-col w-full py-4"
        cellClassName="py-2 lg:py-6"
        className="border-t border-grey lg:border-t-0"
      />
    </div>
  );
};

export default ActivitiesTable;
